import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, KPI, Card, Tag, fmt } from "../components/UI";
import { ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar, Tooltip, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { Sparkles, TrendingUp, AlertTriangle, Shield } from "lucide-react";
import { TimeFilter, periodRange } from "../components/TimeFilter";
import api from "../lib/api";

function Clk({ to, children }) {
  const nav = useNavigate();
  return <div onClick={() => nav(to)} className="cursor-pointer transition-shadow hover:shadow-sm">{children}</div>;
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [preset, setPreset] = useState("this_month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const nav = useNavigate();

  const range = useMemo(() => periodRange(preset, customStart, customEnd), [preset, customStart, customEnd]);

  useEffect(() => { (async () => {
    const params = {};
    if (range.start) params.start_date = range.start;
    if (range.end) params.end_date = range.end;
    const { data } = await api.get("/analytics/executive", { params });
    setData(data);
  })(); }, [range.start, range.end]);

  if (!data) return <div className="text-slate-400 text-sm">Loading dashboard…</div>;
  const k = data.kpis;

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Executive Briefing · Q3 2026"
        title="Champion Behind the Champions"
        subtitle="A board-grade view of how Jenburkt's flagship loyalty engine, powering the ZIXA Strong franchise, is performing across India today. Every tile, row and chart drills through to the underlying records."
        testId="dashboard-page"
      />

      <TimeFilter
        preset={preset} setPreset={setPreset}
        customStart={customStart} setCustomStart={setCustomStart}
        customEnd={customEnd} setCustomEnd={setCustomEnd}
        testId="exec-time-filter"
      />

      {/* KPI Row — every tile is clickable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Clk to="/physios"><KPI label="Enrolled physios" value={fmt(k.enrolled_physios)} testId="kpi-enrolled-physios" /></Clk>
        <Clk to="/physios"><KPI label="Active physios" value={fmt(k.active_physios)} delta="bought in last 6 months" testId="kpi-active-physios" /></Clk>
        <Clk to="/physios"><KPI label="Inactive physios" value={fmt(k.inactive_physios)} deltaDir="neg" delta="no order in 6 months" testId="kpi-inactive-physios" /></Clk>
        <Clk to="/reports/transactions"><KPI label="Total orders" value={fmt(k.total_orders)} delta="unique orders, value > 0" testId="kpi-total-orders" /></Clk>
        <Clk to="/reports/transactions"><KPI label="Order points" value={fmt(k.order_points)} suffix="₹" delta="PTR × order value" testId="kpi-order-points" /></Clk>
        <Clk to="/reports/transactions"><KPI label="Validated points" value={fmt(k.validated_points)} suffix="₹" delta="PTR × dispatch value" testId="kpi-validated-points" /></Clk>
        <Clk to="/milestones"><KPI label="Milestones unlocked" value={fmt(k.milestones_unlocked)} testId="kpi-milestones-unlocked" /></Clk>
        <Clk to="/inventory"><KPI label="Redemptions" value={fmt(k.redemptions)} testId="kpi-redemptions" /></Clk>
      </div>

      {/* Point status legend */}
      <Card title="Point status definitions" subtitle="A shared dictionary across every wallet and report.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[12.5px]">
          <div className="surface p-3"><Tag tone="amber">Pending</Tag><p className="mt-2 text-slate-600">Points earned the moment an order is placed via Field Assist. Awaiting delivery validation (48-hour SLA).</p></div>
          <div className="surface p-3"><Tag tone="emerald">Active</Tag><p className="mt-2 text-slate-600">Validated points after delivery confirmation. Counts toward milestones and is redeemable in the catalog.</p></div>
          <div className="surface p-3"><Tag tone="slate">Redeemed</Tag><p className="mt-2 text-slate-600">Points already spent on a reward. Reduces active liability and increases redemption value.</p></div>
        </div>
      </Card>

      {/* Trend + state perf */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" title="Loyalty trajectory" subtitle="Registrations · Active points · Redemptions — click trend lines to drill in">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.trend}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0F766E" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0F766E" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0A2540" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#0A2540" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="active_points" name="Active points" stroke="#0F766E" strokeWidth={2} fill="url(#g1)" />
                <Area type="monotone" dataKey="registrations" name="Registrations" stroke="#0A2540" strokeWidth={2} fill="url(#g2)" />
                <Line type="monotone" dataKey="redemptions" name="Redemptions" stroke="#be123c" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="City performance" subtitle="Top cities by physios — click a bar to drill in" testId="exec-city-performance">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.city_performance} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis dataKey="city" type="category" width={100} tick={{ fontSize: 11, fill: "#0F172A" }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="physios" name="Physios" fill="#0F766E" radius={[0, 4, 4, 0]} cursor="pointer" onClick={(e) => nav(`/physios?city=${encodeURIComponent(e.city)}`)} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* AI Insights strip */}
      <Card title="AI Insights" subtitle="An optional intelligence layer over rule-based reporting. Sources: physio behaviour, order velocity, redemption mix. Role-gated and audit-logged." action={<button onClick={() => nav("/ai-insights")} className="text-[12px] text-teal-700 hover:underline">View all →</button>}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { to: "/ai-insights", icon: TrendingUp, title: "Growth opportunity", body: "12 high-engagement physios are 1 transaction from unlocking Q3 Platinum. Target via WhatsApp in next 72h." },
            { to: "/ai-insights", icon: AlertTriangle, title: "Churn risk", body: "27 Tier-2 physios show dormancy >65 in last 45d. Recommend 2x bonus reactivation on ZIXA Strong Tab." },
            { to: "/ai-insights", icon: Sparkles, title: "Referral influencers", body: "Top 5 advocates drive 38% of qualified referrals. Launch 'Founding Champion' premium tier." },
            { to: "/audit", icon: Shield, title: "Compliance signal", body: "3 referrals flagged same-clinic pattern (fraud_score>70). Routed to audit queue with full event trail." },
          ].map((i, idx) => (
            <div key={idx} onClick={() => nav(i.to)} className="ai-surface p-3.5 cursor-pointer hover:brightness-95" data-testid={`ai-card-${idx}`}>
              <div className="flex items-center gap-2 text-teal-900 text-[12px] font-semibold"><i.icon size={14} /> {i.title}</div>
              <p className="text-[12.5px] text-teal-900/80 mt-1.5 leading-relaxed">{i.body}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Top products (Point 21 — SKU name + Type) */}
      <Card title="Top ZIXA Strong products driving points" subtitle="Sales & sample SKUs by order value">
        <table className="refined" data-testid="exec-top-products">
          <thead><tr>
            <th>SKU</th><th>Type</th><th className="text-right">Units</th><th className="text-right">Order value (₹)</th><th className="text-right">Points driven</th>
          </tr></thead>
          <tbody>
            {data.top_products.map((p) => (
              <tr key={p.sku} onClick={() => nav(`/inventory?sku=${encodeURIComponent(p.sku)}`)} className="cursor-pointer">
                <td className="font-medium text-slate-900">{p.name || p.sku}</td>
                <td><Tag tone={p.type === "Sample" ? "amber" : "teal"}>{p.type}</Tag></td>
                <td className="numerals text-right">{fmt(p.units)}</td>
                <td className="numerals text-right">{fmt(p.order_value)}</td>
                <td className="numerals text-right">{fmt(p.points_driven)}</td>
              </tr>
            ))}
            {data.top_products.length === 0 && <tr><td colSpan={5} className="text-center text-slate-400 py-6 text-[12px]">No product data for this period.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
